// src/pages/AdminLessons.jsx 
import React, { useState, useEffect, useRef } from "react";
import AdminGate from "@/components/admin/AdminGate";
import { Lesson } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle, Edit, Trash2, Upload as UploadIcon, Music2 } from "lucide-react";
import LessonForm from "@/components/admin/LessonForm";

const ricompenseQuestions = [
  { id: "ricompense_header", type: "header", question: "Ricompense e Obiettivi" },
  { id: "ricompensa_1_giorno", type: "text", question: "Cosa ti regalerai dopo 1 giorno senza fumare?" },
  { id: "ricompensa_3_giorni", type: "text", question: "Cosa ti regalerai dopo 3 giorni senza fumare?" },
  { id: "ricompensa_1_settimana", type: "text", question: "Cosa ti regalerai dopo 1 settimana senza fumare?" },
  { id: "ricompensa_2_settimane", type: "text", question: "Cosa ti regalerai dopo 2 settimane senza fumare?" },
  { id: "ricompensa_1_mese", type: "text", question: "Cosa ti regalerai dopo 1 mese senza fumare?" },
  { id: "ricompensa_2_mesi", type: "text", question: "Cosa ti regalerai dopo 2 mesi senza fumare?" },
  { id: "ricompensa_6_mesi", type: "text", question: "Cosa ti regalerai dopo 6 mesi senza fumare?" },
  { id: "ricompensa_1_anno", type: "text", question: "Cosa ti regalerai dopo 1 anno senza fumare?" },
  { id: "ricompensa_2_anni", type: "text", question: "Cosa ti regalerai dopo 2 anni senza fumare?" },
  { id: "ricompensa_5_anni", type: "text", question: "Cosa ti regalerai dopo 5 anni senza fumare?" },
];

const ADMIN_KEY_STORAGE = "RB_ADMIN_KEY";

// utilità slug dal titolo
const slugify = (s = "") =>
  s.toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 64);

// estensioni audio supportate
const AUDIO_EXTS = [".mp3", ".m4a", ".aac", ".wav", ".ogg", ".oga", ".flac"];

// regex per “lezione 0/1/2 …”
const RX_INDEX = /(?:lezione|lesson)[ _-]*(\d+)/i;
const getIndexFromKey = (key) => {
  const m = String(key).match(RX_INDEX);
  return m ? Number(m[1]) : null;
};

/** Risoluzione “forte” dal bucket: cerca in audio/ per ID, poi slug, poi indice */
async function resolveAudioUrlByScan(lesson) {
  try {
    const r2 = await import("@/api/r2").catch(() => ({}));
    const list = r2.list || r2.default?.list;
    const publicUrl = r2.publicUrl || r2.default?.publicUrl;
    if (!list || !publicUrl) return null;

    const keys = await list("audio/");
    if (!Array.isArray(keys) || !keys.length) return null;

    const lowId = String(lesson.id || "").toLowerCase();
    const lowSlug = slugify(lesson.title || "");
    const orderIx = Number(lesson.order || 0);

    const isAudio = (k) => AUDIO_EXTS.some((ext) => String(k).toLowerCase().endsWith(ext));

    // 1) match per ID
    let hit = keys.find((k) => String(k).toLowerCase().includes(lowId) && isAudio(k));
    // 2) match per slug titolo
    if (!hit && lowSlug) hit = keys.find((k) => String(k).toLowerCase().includes(lowSlug) && isAudio(k));
    // 3) match per indice “lezione N”
    if (!hit) {
      const candidates = keys.filter((k) => getIndexFromKey(k) === orderIx && isAudio(k));
      candidates.sort((a, b) => a.localeCompare(b));
      hit = candidates[0];
    }
    return hit ? publicUrl(hit) : null;
  } catch {
    return null;
  }
}

/** Costruisce la rappresentazione pubblica includendo praticamente TUTTO,
 *  con una deny-list per i soli campi amministrativi/sensibili.
 */
const PRIVATE_KEYS = new Set([
  "published",
  "release_at",
  "created_at",
  "updated_at",
  "deleted_at",
  "owner_id",
  "user_id",
  "author_id",
  "tenant_id",
]);

function toPublicLesson(lesson, audioUrl) {
  const out = {};
  for (const [k, v] of Object.entries(lesson || {})) {
    if (PRIVATE_KEYS.has(k)) continue;
    if (k === "audio_url") continue; // lo settiamo sotto
    out[k] = v;
  }
  out.audio_url = audioUrl || lesson.audio_url || null;
  return out;
}

function AdminLessonsInner() {
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadLessons();
  }, []);

  async function loadLessons() {
    setIsLoading(true);
    try {
      const all = await Lesson.list("order"); // in admin vediamo tutto
      setLessons(all);
    } catch (error) {
      console.error("Errore caricamento lezioni:", error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleEdit(lesson) {
    setSelectedLesson(lesson);
    setIsFormOpen(true);
  }

  function handleCreate() {
    setSelectedLesson(null);
    setIsFormOpen(true);
  }

  async function handleDelete(lessonId) {
    if (!window.confirm("Sei sicuro di voler eliminare questa lezione?")) return;
    try {
      await Lesson.delete(lessonId);
      await loadLessons();
    } catch (error) {
      console.error("Errore eliminazione lezione:", error);
      alert("Impossibile eliminare la lezione.");
    }
  }

  async function handleFormSubmit(data) {
    try {
      if (selectedLesson) {
        await Lesson.update(selectedLesson.id, { ...selectedLesson, ...data });
      } else {
        await Lesson.create({
          published: true,
          release_at: new Date().toISOString(),
          ...data,
        });
      }
      setIsFormOpen(false);
      setSelectedLesson(null);
      await loadLessons();
    } catch (error) {
      console.error("Errore salvataggio lezione:", error);
      alert("Si è verificato un errore durante il salvataggio.");
    }
  }

  function handleFormCancel() {
    setIsFormOpen(false);
    setSelectedLesson(null);
  }

  async function togglePublished(lesson) {
    const next = !lesson.published;
    try {
      await Lesson.update(lesson.id, {
        published: next,
        ...(next && !lesson.release_at ? { release_at: new Date().toISOString() } : {}),
      });
      await loadLessons();
    } catch (error) {
      console.error("Errore cambio stato pubblicazione:", error);
      alert("Errore durante il cambio di stato.");
    }
  }

  // ------------------- COSTRUZIONE CATALOGO PUBBLICO con AUDIO -------------------

  const now = Date.now();
  const isReleased = (l) => {
    if (!l?.release_at) return true;
    const t = Date.parse(l.release_at);
    return Number.isFinite(t) ? t <= now : true;
  };

  /** Costruisce il catalogo completo:
   *  - ordina le lezioni visibili
   *  - risolve/l’assegna l’audio
   *  - include quasi tutti i campi della lezione (deny-list minimale)
   */
  async function buildPublicCatalog() {
    const r2 = await import("@/api/r2").catch(() => ({}));
    const list = r2.list || r2.default?.list;
    const publicUrl = r2.publicUrl || r2.default?.publicUrl;

    // mappa indice -> array di chiavi ordinate (per eventuale fallback)
    const groups = new Map();

    // [RB PATCH] elenco completo chiavi per normalizzare i sottofondi
    let allAudioKeys = [];

    if (list && publicUrl) {
      const keys = await list("audio/");
      allAudioKeys = keys || [];
      for (const k of allAudioKeys) {
        const ix = getIndexFromKey(k);
        if (ix == null) continue;
        if (!groups.has(ix)) groups.set(ix, []);
        groups.get(ix).push(k);
      }
      for (const arr of groups.values()) arr.sort((a, b) => a.localeCompare(b));
    }

    const takeFromGroup = (ix) => {
      if (!publicUrl) return null;
      const arr = groups.get(ix);
      if (!arr || !arr.length) return null;
      const key = arr.shift();
      return publicUrl(key);
    };

    // [RB PATCH] helpers per normalizzare URL dei sottofondi
    const basenameOf = (s) => {
      let b = String(s || "").split("/").pop() || "";
      try { b = decodeURIComponent(b); } catch {}
      return b.toLowerCase();
    };
    const findKeyByBasename = (name) => {
      const low = basenameOf(name);
      return (allAudioKeys || []).find(k => basenameOf(k) === low) || null;
    };

    // >>>>>>>>>>>>>>>>>>>>  MODIFICA UNICA QUI  <<<<<<<<<<<<<<<<<<<<<<
    const normalizeBgUrl = (u) => {
      if (!u) return "";

      const ensureMediaUrl = (filename) => {
        const key = findKeyByBasename(filename);
        if (key && publicUrl) return publicUrl(key);            // URL R2 corretto
        return `/api/media/${encodeURIComponent(`audio/${filename}`)}`; // fallback proxy
      };

      // Se è un URL assoluto…
      try {
        const abs = new URL(String(u).trim(), window.location.origin);

        // già su /api/media → ok
        if (abs.pathname.startsWith("/api/media/")) return abs.toString();

        // nostro dominio ma NON /api → interpreta come nome file
        if (abs.origin === window.location.origin && !abs.pathname.startsWith("/api/")) {
          const filename = abs.pathname.split("/").pop();
          return ensureMediaUrl(filename);
        }

        // dominio esterno: lascialo com’è
        return abs.toString();
      } catch {
        // non è un URL: trattalo come nome file
        const filename = String(u || "").split("/").pop();
        return ensureMediaUrl(filename);
      }
    };
    // >>>>>>>>>>>>>>>>>>>>  FINE MODIFICA  <<<<<<<<<<<<<<<<<<<<<<

    const candidates = lessons
      .filter((l) => l.published && isReleased(l))
      .sort(
        (a, b) =>
          (Number(a.order || 0) - Number(b.order || 0)) ||
          String(a.title || "").localeCompare(String(b.title || ""))
      );

    const out = [];
    for (const l of candidates) {
      let audioUrl = l.audio_url || (await resolveAudioUrlByScan(l)) || takeFromGroup(Number(l.order || 0));

      const pl = toPublicLesson(l, audioUrl);

      // [RB PATCH] normalizza le opzioni di sottofondo (accetta nome file o URL)
      if (Array.isArray(l.background_audio_options) && l.background_audio_options.length) {
        pl.background_audio_options = l.background_audio_options
          .map(opt => ({
            name: opt?.name || "",
            url: normalizeBgUrl(opt?.url || ""),
          }))
          .filter(o => !!o.url);
      }

      out.push(pl);
    }
    return out;
  }

  // ---------- Helpers catalogo pubblico (merge/backup/ripristino) ----------
  async function fetchPublicCatalog() {
    try {
      const res = await fetch("/api/content", { headers: { accept: "application/json" } });
      if (!res.ok) return {};
      return await res.json();
    } catch {
      return {};
    }
  }
  const lessonKey = (l) =>
    (l?.id ? `id:${l.id}` :
      l?.order != null ? `ord:${Number(l.order)}` :
      `title:${String(l?.title || "").toLowerCase()}`);

  const sortLessons = (a, b) =>
    (Number(a.order || 0) - Number(b.order || 0)) ||
    String(a.title || "").localeCompare(String(b.title || ""));

  function downloadJson(filename, data) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ---------- Pubblicazione: MERGE (default) / REPLACE ----------
  async function publishCatalogToServer({ mode = "merge" } = {}) {
    try {
      const newLessons = await buildPublicCatalog();
      if (!newLessons.length) {
        alert("Nessuna lezione pubblicata/rilasciata da inviare.");
        return;
      }

      // prendo il catalogo attuale per preservare 'extras' e qualsiasi altro campo
      const current = await fetchPublicCatalog();

      let payload;
      if (mode === "replace") {
        // sovrascrivo solo la sezione "lessons", ma preservo gli altri campi (extras ecc.)
        payload = {
          ...current,
          version: Date.now(),
          lessons: [...newLessons].sort(sortLessons),
        };
      } else {
        // MERGE sicuro: unisci per chiave stabile
        const prevLessons = Array.isArray(current?.lessons)
          ? current.lessons
          : (Array.isArray(current) ? current : []);
        const map = new Map();
        for (const l of prevLessons) map.set(lessonKey(l), l);
        for (const l of newLessons) map.set(lessonKey(l), { ...(map.get(lessonKey(l)) || {}), ...l });

        payload = {
          ...current, // preserva extras/altri campi
          version: Date.now(),
          lessons: Array.from(map.values()).sort(sortLessons),
        };
      }

      let key = localStorage.getItem(ADMIN_KEY_STORAGE) || "";
      key = window.prompt("Inserisci la ADMIN_KEY per pubblicare il catalogo:", key || "");
      if (!key) return;
      localStorage.setItem(ADMIN_KEY_STORAGE, key);

      const res = await fetch(`/api/content?key=${encodeURIComponent(key)}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data?.error === "KEY_MISMATCH") localStorage.removeItem(ADMIN_KEY_STORAGE);
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      alert(`Catalogo pubblicato (${payload.lessons.length} lezioni). Modalità: ${mode.toUpperCase()}`);
    } catch (e) {
      console.error("Errore pubblicazione catalogo:", e);
      alert(`Errore pubblicazione catalogo: ${String(e.message || e)}`);
    }
  }

  async function backupCatalog() {
    const cat = await fetchPublicCatalog();
    const name = `catalog-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
    downloadJson(name, cat);
  }

  async function restoreCatalogFromFile(file) {
    try {
      const text = await file.text();
      const json = JSON.parse(text);

      let key = localStorage.getItem(ADMIN_KEY_STORAGE) || "";
      key = window.prompt("Inserisci la ADMIN_KEY per ripristinare il catalogo:", key || "");
      if (!key) return;
      localStorage.setItem(ADMIN_KEY_STORAGE, key);

      const res = await fetch(`/api/content?key=${encodeURIComponent(key)}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...json, version: Date.now() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      alert("Catalogo ripristinato con successo.");
    } catch (e) {
      console.error("Ripristino catalogo fallito:", e);
      alert("Errore nel ripristino. Controlla il file JSON.");
    }
  }

  async function importFromPublicCatalog() {
    try {
      const payload = await fetchPublicCatalog();
      const incoming = Array.isArray(payload?.lessons) ? payload.lessons : (Array.isArray(payload) ? payload : []);
      if (!incoming.length) {
        alert("Nessuna lezione trovata nel catalogo pubblico.");
        return;
      }

      const current = await Lesson.list("order");
      const byOrder = new Map(current.map(l => [Number(l.order || 0), l]));
      const byTitle = new Map(current.map(l => [String(l.title || "").toLowerCase(), l]));

      let created = 0, updated = 0;
      for (const pl of incoming) {
        const match =
          byOrder.get(Number(pl.order || 0)) ||
          byTitle.get(String(pl.title || "").toLowerCase()) ||
          null;

        const patch = {
          title: pl.title || "",
          description: pl.description || "",
          audio_url: pl.audio_url || "",
          duration: Number(pl.duration || 0),
          order: Number(pl.order || 0),

          exercise_instructions: pl.exercise_instructions || "",
          exercise_question: pl.exercise_question || "",
          exercise_type: pl.exercise_type || "text_input",
          exercise_options: pl.exercise_options || [],
          exercise_structured_questions: pl.exercise_structured_questions || [],
          scored_test_multiple_choice: pl.scored_test_multiple_choice || [],
          scored_test_open_questions: pl.scored_test_open_questions || [],
          background_audio_options: pl.background_audio_options || [],

          published: true,
          release_at: new Date().toISOString(),
        };

        if (match?.id) {
          await Lesson.update(match.id, { ...match, ...patch });
          updated++;
        } else {
          await Lesson.create(patch);
          created++;
        }
      }

      await loadLessons();
      alert(`Import completato: ${created} create, ${updated} aggiornate.`);
    } catch (e) {
      console.error("Import catalogo pubblico fallito:", e);
      alert("Errore durante l'import dal catalogo pubblico.");
    }
  }

  const visibleCount = lessons.filter((l) => l.published && isReleased(l)).length;

  // ------------------- PRECOMPILA LEZIONE 6 -------------------
  async function setupLesson6() {
    try {
      const lesson6 = lessons.find((l) => Number(l.order) === 6);
      if (!lesson6) {
        alert("Lezione 6 non trovata. Creala prima.");
        return;
      }
      await Lesson.update(lesson6.id, {
        exercise_type: "structured_questionnaire",
        exercise_structured_questions: ricompenseQuestions,
        exercise_instructions:
          "Definisci le ricompense che ti darai per ogni traguardo raggiunto. Questo ti aiuterà a mantenere alta la motivazione.",
      });
      await loadLessons();
      alert("Lezione 6 configurata con successo!");
    } catch (error) {
      console.error("Errore configurazione Lezione 6:", error);
      alert("Si è verificato un errore durante la configurazione.");
    }
  }

  // ----------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Gestione Lezioni</h1>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 mr-2">
              Pubblicate/visibili: <b>{visibleCount}</b> / {lessons.length}
            </span>

            {/* Sicurezza: backup e ripristino */}
            <Button onClick={backupCatalog} variant="secondary" title="Scarica il JSON corrente di /api/content">
              <UploadIcon className="w-4 h-4 mr-2" />
              Backup catalogo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && restoreCatalogFromFile(e.target.files[0])}
            />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} title="Carica un JSON per ripristinare /api/content">
              Ripristina da file
            </Button>

            {/* Pubblicazione sicura */}
            <Button
              onClick={() => publishCatalogToServer({ mode: "merge" })}
              title="Unisce le lezioni pubblicate con il catalogo esistente (consigliato)"
            >
              Pubblica (merge)
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm("Sei sicuro? Sovrascriverai l'elenco lezioni nel catalogo pubblico (gli EXTRA e altri campi resteranno invariati).")) {
                  publishCatalogToServer({ mode: "replace" });
                }
              }}
              title="Sovrascrive SOLO la sezione lezioni. Gli 'extras' e altri campi restano invariati."
            >
              Sovrascrivi tutto
            </Button>

            {/* Utility */}
            <Button onClick={importFromPublicCatalog} variant="outline" title="Ricrea/aggiorna le lezioni locali dal catalogo pubblicato">
              Importa dal catalogo
            </Button>

            <Button onClick={setupLesson6} variant="outline">
              Pre-compila Lezione 6
            </Button>

            <Button onClick={handleCreate} className="bg-gradient-to-r from-blue-500 to-purple-600">
              <PlusCircle className="w-4 h-4 mr-2" />
              Nuova Lezione
            </Button>
          </div>
        </div>

        {isFormOpen && (
          <LessonForm lesson={selectedLesson} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
        )}

        <div className="space-y-4">
          {lessons.map((lesson) => (
            <Card key={lesson.id}>
              <CardHeader>
                <div className="flex justify-between items-center gap-3">
                  <div className="flex items-center gap-3">
                    <CardTitle>Lezione {lesson.order}: {lesson.title}</CardTitle>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        lesson.published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}
                      title={lesson.release_at ? `release_at: ${new Date(lesson.release_at).toLocaleString()}` : ""}
                    >
                      {lesson.published ? "Pubblicata" : "Bozza"}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => togglePublished(lesson)}>
                      {lesson.published ? "Metti in bozza" : "Pubblica"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(lesson)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Modifica
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(lesson.id)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Elimina
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">{lesson.description}</p>
                {lesson.exercise_type && (
                  <p className="text-sm text-blue-600 mt-2">Esercizio: {lesson.exercise_type}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                  <Music2 className="w-4 h-4" />
                  <span>Quando pubblichi, l’audio e tutti i dati dell’esercizio sono inclusi nel catalogo.</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {lessons.length === 0 && (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessuna lezione trovata</h3>
            <p className="text-gray-500">Clicca su "Nuova Lezione" per iniziare.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminLessons() {
  return (
    <AdminGate>
      <AdminLessonsInner />
    </AdminGate>
  );
}
